import Vue from 'vue';
import { saveUserPreferences, getUserPreferences } from '@/api/preferences';
import { dlog } from '@/lib/debugLog';
class SessionCountStore {
    state = Vue.observable({
        sessionsPerPage: 5,
        isLoading: false
    });
    get sessionsPerPage() {
        return this.state.sessionsPerPage;
    }
    get isLoading() {
        return this.state.isLoading;
    }
    get isShowingAll() {
        return this.state.sessionsPerPage === 'all';
    }
    async initialize() {
        dlog('[SessionCountStore] Initializing session count store');
        try {
            this.state.isLoading = true;
            // First check localStorage for immediate UI update
            const savedCount = localStorage.getItem('sessionsPerPage');
            dlog('[SessionCountStore] Saved count from localStorage:', savedCount);
            if (savedCount) {
                this.setSessionsPerPage(savedCount === 'all' ? 'all' : parseInt(savedCount));
            }
            // Then fetch from server
            const preferences = await getUserPreferences();
            dlog('[SessionCountStore] Preferences from server:', preferences);
            // Only use server preference if we don't have a local one
            // This prevents the server from overriding user's choice
            if (preferences?.sessionsPerPage && !savedCount) {
                this.setSessionsPerPage(preferences.sessionsPerPage);
            }
            else if (savedCount && preferences?.sessionsPerPage !== savedCount) {
                // If local and server differ, save local preference to server
                this.savePreferences();
            }
        }
        catch (error) {
            console.error('[SessionCountStore] Failed to load preferences:', error);
            // Use default of 5 sessions as fallback
            this.setSessionsPerPage(5);
        }
        finally {
            this.state.isLoading = false;
        }
    }
    setSessionsPerPage(count) {
        dlog('[SessionCountStore] Setting sessions per page to:', count);
        this.state.sessionsPerPage = count;
        // Save to localStorage for immediate persistence
        localStorage.setItem('sessionsPerPage', count.toString());
        dlog('[SessionCountStore] Sessions per page set, localStorage updated');
    }
    async updateSessionsPerPage(count) {
        this.setSessionsPerPage(count);
        // Save to server (don't await to keep UI responsive)
        this.savePreferences();
    }
    async savePreferences() {
        try {
            const saved = await saveUserPreferences({ sessionsPerPage: this.state.sessionsPerPage });
            if (saved === true) {
                dlog('[SessionCountStore] Preferences saved successfully');
            }
            else if (saved === false) {
                console.warn('[SessionCountStore] Preferences not saved to server (request failed)');
            }
        }
        catch (error) {
            console.error('[SessionCountStore] Failed to save preferences:', error);
            // Continue working even if save fails
        }
    }
    // Get display label for UI
    getDisplayLabel() {
        switch (this.state.sessionsPerPage) {
            case 3: return 'Show 3 sessions';
            case 5: return 'Show 5 sessions';
            case 10: return 'Show 10 sessions';
            case 20: return 'Show 20 sessions';
            case 'all': return 'Show all sessions';
            default: return 'Show 5 sessions';
        }
    }
    // Get available options for dropdown
    getAvailableOptions() {
        return [
            { value: 3, label: 'Show 3 sessions' },
            { value: 5, label: 'Show 5 sessions' },
            { value: 10, label: 'Show 10 sessions' },
            { value: 20, label: 'Show 20 sessions' },
            { value: 'all', label: 'Show all sessions' }
        ];
    }
}
export const sessionCountStore = new SessionCountStore();
